import { Form, Button, Tabs, useToaster, Message, IconButton } from "rsuite";
import PlusIcon from '@rsuite/icons/Plus';
import CloseIcon from '@rsuite/icons/Close';
import { useOrderCreation } from "../context/OrderCreationContext";
import { OrderType } from "../../../../../my-enum";
import { authUserData } from "../../../../../service";
import { getServiceFeeDataForTeam } from "../../../../../service/VehicleService";
import { calculateServiceFee } from "../utils/orderCalculations";
import GooglePlacesAutocomplete from "../../../../../components/GooglePlacesAutocomplete";

const messageWarningPush = (strMessage: string) => (
  <Message showIcon type="warning" closable>
    {strMessage}
  </Message>
);

export const AddressSelection: React.FC = () => {
  const {
    orderType,
    setOrderType,
    selectedAdress,
    selectedTerminal,
    rideTime,
    setStep,
    setFlightSchedule,
    setIsRidetimeTooLateOrEarly,
    setIsShowLoading,
    setEstArriveTime,
    setAddServiceFee,
    selectedPlace,
    viaPoints,
    setViaPoints,
    setSegmentDistances
  } = useOrderCreation();

  const toaster = useToaster();
  const authUser = authUserData();

  const handleAddViaPoint = () => {
    if (viaPoints.length < 2) {
      setViaPoints([...viaPoints, '']);
    }
  };

  const handleRemoveViaPoint = (index: number) => {
    const newViaPoints = viaPoints.filter((_, i) => i !== index);
    setViaPoints(newViaPoints);
  };

  const handleViaPointChange = (index: number, value: string) => {
    const newViaPoints = [...viaPoints];
    newViaPoints[index] = value;
    setViaPoints(newViaPoints);
  };

  const handleNext = async () => {
    if (!selectedAdress) {
      toaster.push(messageWarningPush("地址為必填欄位"), { placement: 'bottomCenter', duration: 2000 });
      return;
    }
    if (!selectedTerminal) {
      toaster.push(
        messageWarningPush(`${orderType === OrderType.送機 ? '送機' : '接機'}航廈為必填欄位`),
        { placement: 'bottomCenter', duration: 2000 }
      );
      return;
    }

    setIsShowLoading(true);

    // Calculate route time considering via points
    const distanceMatrixService = new google.maps.DistanceMatrixService();

    // Build the route segments: start -> via1 -> via2 -> end
    const filteredViaPoints = viaPoints.filter(point => point.trim() !== '');
    const routeSegments: string[] = [];

    if (orderType === OrderType.送機) {
      // 送機: pickup address -> via points -> terminal
      routeSegments.push(selectedAdress);
      routeSegments.push(...filteredViaPoints);
      routeSegments.push(selectedTerminal.name);
    } else {
      // 接機: terminal -> via points -> drop-off address
      routeSegments.push(selectedTerminal.name);
      routeSegments.push(...filteredViaPoints);
      routeSegments.push(selectedAdress);
    }

    // Calculate total duration by summing each segment
    let totalDurationSeconds = 0;
    const segmentDistancesArray: number[] = [];

    const calculateSegmentDuration = async (index: number) => {
      if (index >= routeSegments.length - 1) {
        // All segments calculated
        let arriveTime = new Date(rideTime);
        arriveTime.setSeconds(arriveTime.getSeconds() + totalDurationSeconds);
        setEstArriveTime(arriveTime);

        // Store segment distances
        setSegmentDistances(segmentDistancesArray);

        // Continue with the rest of the flow
        setFlightSchedule(undefined);
        setIsRidetimeTooLateOrEarly(false);

        // Check if address is in service area (only for 送機)
        if (orderType === OrderType.送機 && selectedPlace) {
          const serviceFeeData = await getServiceFeeDataForTeam(authUser?.team.id);
          if (!serviceFeeData) {
            toaster.push(
              messageWarningPush('無法取得服務費用資料'),
              { placement: 'bottomCenter', duration: 2000 }
            );
            setIsShowLoading(false);
            return;
          }
          const { isAddressInService, addServiceFee } = await calculateServiceFee(
            selectedPlace,
            selectedTerminal,
            serviceFeeData,
            authUser?.team.code!
          );

          if (isAddressInService) {
            setAddServiceFee(addServiceFee);
            console.log(`Add service fee: ${addServiceFee}`);
            setStep(2);
          } else {
            alert('地址不在服務範圍');
          }
        } else {
          setStep(2);
        }

        setIsShowLoading(false);
        return;
      }

      const request = {
        origins: [routeSegments[index]],
        destinations: [routeSegments[index + 1]],
        travelMode: google.maps.TravelMode.DRIVING
      };

      distanceMatrixService.getDistanceMatrix(request, function (result, status) {
        if (status === 'OK' && result!.rows[0].elements[0].status === 'OK') {
          totalDurationSeconds += result!.rows[0].elements[0].duration.value;
          const distanceMeters = result!.rows[0].elements[0].distance.value;
          segmentDistancesArray.push(distanceMeters);
          calculateSegmentDuration(index + 1);
        } else {
          console.error(`Failed to calculate route segment ${index} to ${index + 1}`);
          setIsShowLoading(false);
          toaster.push(
            messageWarningPush('路線計算失敗，請檢查地址是否正確'),
            { placement: 'bottomCenter', duration: 2000 }
          );
        }
      });
    };

    // Start calculating from the first segment
    calculateSegmentDuration(0);
  };

  return (
    <Tabs
      defaultActiveKey={orderType === OrderType.送機 ? 'FlightOutTab' : 'FlightInTab'}
      appearance="subtle"
      onSelect={(eventKey) => {
        if (eventKey === 'FlightOutTab') {
          setOrderType(OrderType.送機);
        } else {
          setOrderType(OrderType.接機);
        }
      }}
    >
      <Tabs.Tab eventKey="FlightOutTab" title="送機">
        <br />
        <Form style={{ paddingLeft: '15px', paddingRight: '15px' }} fluid>
          <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
            <Form.ControlLabel>上車地址</Form.ControlLabel>
            <Form.Control
              name="pickupAddress"
              placeholder="輸入上車地址"
              onClick={() => setStep(7)}
              value={selectedAdress}
            />
          </Form.Group>

          {/* Via Points Section */}
          {viaPoints.map((viaPoint, index) => (
            <Form.Group key={index} className="mb-3" style={{ textAlign: 'left' }}>
              <Form.ControlLabel>
                經由地點 {index + 1}
                <IconButton
                  icon={<CloseIcon />}
                  size="xs"
                  appearance="subtle"
                  onClick={() => handleRemoveViaPoint(index)}
                  style={{ marginLeft: '10px' }}
                />
              </Form.ControlLabel>
              <GooglePlacesAutocomplete
                value={viaPoint}
                onChange={(value) => handleViaPointChange(index, value)}
                placeholder={`輸入經由地點 ${index + 1}`}
              />
            </Form.Group>
          ))}

          {viaPoints.length < 2 && (
            <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
              <Button
                appearance="link"
                onClick={handleAddViaPoint}
                startIcon={<PlusIcon />}
              >
                新增經由地點 ({viaPoints.length}/2)
              </Button>
            </Form.Group>
          )}

          <br />
          <Form.Group className="d-grid">
            <Button appearance="primary" style={{ width: '100px' }} onClick={handleNext}>
              選擇車種
            </Button>
          </Form.Group>
        </Form>
        <br />
      </Tabs.Tab>

      <Tabs.Tab eventKey="FlightInTab" title="接機">
        <br />
        <Form style={{ paddingLeft: '15px', paddingRight: '15px' }} fluid>


          <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
            <Form.ControlLabel>下車地址</Form.ControlLabel>
            <Form.Control
              name="pickupAddress"
              placeholder="輸入下車地址"
              onClick={() => setStep(7)}
              value={selectedAdress}
            />
          </Form.Group>

          {/* Via Points Section */}
          {viaPoints.map((viaPoint, index) => (
            <Form.Group key={index} className="mb-3" style={{ textAlign: 'left' }}>
              <Form.ControlLabel>
                經由地點 {index + 1}
                <IconButton
                  icon={<CloseIcon />}
                  size="xs"
                  appearance="subtle"
                  onClick={() => handleRemoveViaPoint(index)}
                  style={{ marginLeft: '10px' }}
                />
              </Form.ControlLabel>
              <GooglePlacesAutocomplete
                value={viaPoint}
                onChange={(value) => handleViaPointChange(index, value)}
                placeholder={`輸入經由地點 ${index + 1}`}
              />
            </Form.Group>
          ))}

          {viaPoints.length < 2 && (
            <Form.Group className="mb-3" style={{ textAlign: 'left' }}>
              <Button
                appearance="link"
                onClick={handleAddViaPoint}
                startIcon={<PlusIcon />}
              >
                新增經由地點 ({viaPoints.length}/2)
              </Button>
            </Form.Group>
          )}

          <br />
          <Form.Group className="d-grid">
            <Button appearance="primary" style={{ width: '100px' }} onClick={handleNext}>
              選擇車種
            </Button>
          </Form.Group>
        </Form>
        <br />
      </Tabs.Tab>
    </Tabs>
  );
};
